"use client";

import React, { useEffect, useState } from "react";
import { Switch } from "../ui/switch";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "inaccessibility-settings";

type Settings = Record<string, boolean | string>;

function getSettings(): Settings {
    if (typeof window === "undefined") return {};
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    } catch {
        return {};
    }
}

function saveSettings(settings: Settings) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}


const InaccessabilityPopup = ({setGlassesEnabled, setNudgeOpen}: {setGlassesEnabled: (a: boolean)=>void,setNudgeOpen: (a: boolean)=>void}) => {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);

    const togglePopup = () => {
        setOpen((prev) => !prev);
        setNudgeOpen(false)
        localStorage.setItem("nudge", "true")
    }
    /* Restore saved settings on mount */
    useEffect(() => {
        const saved = getSettings();

        Object.entries(saved).forEach(([key, value]) => {
            if (key === "emoji-lang") {
                i18n.changeLanguage(value as boolean ? "emji" : "en");
                return;
            }
            else if (key === "glasses-friendly") {
                setGlassesEnabled(value as boolean);
                return;
            }
            else if (value === true) {
                document.documentElement.classList.add(key);
            }
        });
    }, [i18n]);

    const accessibilityItems = [
        {
            settingKey: "low-contrast-mode",
            icon: "/img/icons/Sunset.png",
            name: "Low Contrast",
            action: () =>
                document.documentElement.classList.add("low-contrast-mode"),
            unaction: () =>
                document.documentElement.classList.remove("low-contrast-mode"),
        },
        {
            settingKey: "small-text-mode",
            icon: "/img/icons/Tt.png",
            name: "Unmagnifier",
            action: () =>
                document.documentElement.classList.add("small-text-mode"),
            unaction: () =>
                document.documentElement.classList.remove("small-text-mode"),
        },
        {
            settingKey: "emoji-lang",
            icon: "/img/icons/Emji.png",
            name: "Screen Unreader",
            action: () => {
                i18n.changeLanguage("emji");
            },
            unaction: () => {
                i18n.changeLanguage("en");
            },
        },
        {
            settingKey: "epilepsy-mode",
            icon: "/img/icons/Flash.png",
            name: "Enable Flashing",
            action: () =>
                document.documentElement.classList.add("epilepsy-mode"),
            unaction: () =>
                document.documentElement.classList.remove("epilepsy-mode"),
        },
        {
            settingKey: "dim-mode",
            icon: "/img/icons/Moon.png",
            name: "Dim Mode",
            action: () => document.documentElement.classList.add("dim-mode"),
            unaction: () => document.documentElement.classList.remove("dim-mode"),
        },
        {
            settingKey: "glasses-friendly",
            icon: "/img/icons/Glasses.png",
            name: "Glasses Friendly",
            action: () =>
                setGlassesEnabled(true),
            unaction: () =>
                setGlassesEnabled(false),
        },
    ];

    return (
        <div className="fixed bottom-0 right-6 z-50 flex flex-col-reverse items-end pointer-events-auto">
            {!open && (
                <button
                    onClick={togglePopup}
                    className="bg-blue-600 text-white px-4 py-3 rounded-t-4xl shadow-lg hover:bg-blue-700 transition"
                >
                    <div className="flex items-center">
                        <img src="/img/man.png" className="h-[30px] rotate-180" alt="icon" />
                        <span className="pl-2">Inaccessibility Settings</span>
                    </div>
                </button>
            )}

            {open && (
                <div className="mb-2 w-96 md:w-[32rem] bg-white shadow-2xl rounded-lg overflow-hidden flex flex-col">
                    <div className="flex justify-between items-center p-4 bg-blue-600 text-white">
                        <h3 className="font-semibold text-lg">
                            Inaccessibility Settings
                        </h3>
                        <button onClick={togglePopup} className="font-bold text-xl">
                            x
                        </button>
                    </div>

                    <div className="p-4 text-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {accessibilityItems.map((item) => (
                            <AccessibilityItem
                                key={item.settingKey}
                                settingKey={item.settingKey}
                                icon={item.icon}
                                name={item.name}
                                action={item.action}
                                unaction={item.unaction}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

function AccessibilityItem({
    icon,
    name,
    action,
    unaction,
    settingKey,
}: {
    icon: string;
    name: string;
    action: () => void;
    unaction: () => void;
    settingKey: string;
}) {
    const [enabled, setEnabled] = useState(false);

    useEffect(() => {
        const settings = getSettings();
        setEnabled(!!settings[settingKey]);
    }, [settingKey]);

    const handleToggle = () => {
        const next = !enabled;
        setEnabled(next);

        const settings = getSettings();

        if (next) {
            action();
            settings[settingKey] = true;
        } else {
            unaction();
            settings[settingKey] = false;
        }

        saveSettings(settings);
    };

    return (
        <div className="flex items-center justify-between p-2 border rounded-md shadow-sm">
            <div className="flex items-center gap-2">
                <img src={icon} className="h-6 w-6" alt={name} />
                <p>{name}</p>
            </div>
            <Switch checked={enabled} onCheckedChange={handleToggle} />
        </div>
    );
}

export default InaccessabilityPopup;